import { Anchor, Button, Dialog, H3, P, Space } from '@dnb/eufemia';
import { edit as EditIcon, file_medium as FileIcon } from '@dnb/eufemia/icons';
import { copy as CopyIcon } from '@dnb/eufemia/icons';
import { AppWithEntitlementsDto } from '@portals/shared/portal/AppDto';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import Card from '@/components/Card';
import CopyButton from '@/components/CopyButton';
import Divider from '@/components/Divider';
import ServiceNowIdentifierViewer from '@/components/ServiceNowIdentifierViewer';
import useAuth from '@/hooks/useAuth';

import style from './index.module.css';

type AppOwner = {
  id: string;
  name: string;
  type: 'team' | 'personal';
};

type AppDetailsProps = {
  owner: AppOwner;
  createdBy?: string;
  isAdmin: boolean;
  app: AppWithEntitlementsDto;
};

export default function ApplicationDetails({ owner, createdBy, isAdmin, app }: AppDetailsProps): JSX.Element {
  const { isInternal } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <H3 bottom="small">
        <FileIcon /> App details
      </H3>
      <Card>
        <Space bottom="small" className={style['AppDetails-CardRow']}>
          <P className={style['AppDetails-Label']} weight="bold">
            App Id
          </P>
          <P>{app.id}</P>
          <CopyButton icon={CopyIcon} toCopy={app.id} variant="tertiary">
            Copy
          </CopyButton>
        </Space>
        <Space bottom="small" className={style['AppDetails-CardRow']}>
          <P className={style['AppDetails-Label']} weight="bold">
            Description
          </P>
          <P>
            {app.description && app.description.trim().length > 0 ? (
              app.description
            ) : (
              <span className={style['AppDetails-NotProvided']}>Not provided</span>
            )}
          </P>
        </Space>
        {app.project?.name && (
          <Space bottom="small" className={style['AppDetails-CardRow']}>
            <P className={style['AppDetails-Label']} weight="bold">
              Project
            </P>
            <P>{app.project?.name}</P>
            <Dialog
              description={
                <P>
                  An project is an entity that maps to ciam applications. Its primary purpose is to group clients for
                  the purpose of effective tracking.
                </P>
              }
              title="Project"
            >
              <Space bottom="small" className={style['AppDetails-CardRow']} top="small">
                <P className={style['AppDetails-Label']} weight="bold">
                  Project id
                </P>
                <P>{app.project.id}</P>
              </Space>
              <Space bottom="small" className={style['AppDetails-CardRow']}>
                <P className={style['AppDetails-Label']} weight="bold">
                  Project Name
                </P>
                <P>{app.project.name}</P>
              </Space>
              <Space bottom="small" className={style['AppDetails-CardRow']}>
                <P className={style['AppDetails-Label']} weight="bold">
                  Project Description
                </P>
                <P>{app.project.description}</P>
              </Space>
              <Space bottom="small" className={style['AppDetails-CardRow']}>
                <P className={style['AppDetails-Label']} weight="bold">
                  Ciam Uat App Id
                </P>
                <P>{app.project.ciamUatAppId}</P>
              </Space>
              <Space bottom="small" className={style['AppDetails-CardRow']}>
                <P className={style['AppDetails-Label']} weight="bold">
                  Ciam Prod App Id
                </P>
                <P>{app.project.ciamProdAppId}</P>
              </Space>
            </Dialog>
          </Space>
        )}
        <Space bottom="small" className={style['AppDetails-CardRow']}>
          <P className={style['AppDetails-Label']} weight="bold">
            Owner
          </P>
          <Anchor href={owner.type == 'team' ? `/team/${owner.id}` : `/profile/apps`}>{owner.name}</Anchor>
        </Space>
        {createdBy && (
          <Space bottom="small" className={style['AppDetails-CardRow']}>
            <P className={style['AppDetails-Label']} weight="bold">
              Created by
            </P>
            <P>{createdBy}</P>
          </Space>
        )}

        {owner.type == 'team' && isInternal && (
          <Space bottom="small" className={style['AppDetails-CardRow']}>
            <P className={style['AppDetails-Label']} weight="bold">
              ServiceNow Id
            </P>
            <P>
              {app.serviceNowIdentifier && app.serviceNowIdentifier.length > 0 ? (
                app.serviceNowIdentifier
              ) : (
                <span className={style['AppDetails-NotProvided']}>Not provided</span>
              )}
            </P>
            <Dialog
              description={
                <P>
                  The Business application id (BAXXXXX) or Service offering id (BSNXXXXXXX) to identify the consumer in
                  ServiceNow.
                </P>
              }
              title="ServiceNow Identifier (BA/BSN)"
            >
              <Space bottom="small" className={style['AppDetails-CardRow']} top="small">
                {app.serviceNowIdentifier && (
                  <ServiceNowIdentifierViewer onChange={() => {}} serviceNowIdentifier={app.serviceNowIdentifier} />
                )}
              </Space>
            </Dialog>
          </Space>
        )}

        <Divider />
        {isAdmin && (
          <div>
            <Button
              icon={EditIcon}
              iconPosition="left"
              onClick={() => {
                navigate(
                  owner.type === 'team'
                    ? `/team/${owner.id}/application/${app.id}/edit`
                    : `/application/${app.id}/edit`,
                  {
                    replace: true,
                  },
                );
              }}
              text="Edit"
              top="small"
              variant="secondary"
            />
          </div>
        )}
      </Card>
    </>
  );
}
